import { Sun, Flame, Sparkles, Compass } from "lucide-react";

// Symbol components
const SYMBOLS = {
  sun: Sun,
  flame: Flame,
  star: Sparkles,
  compass: Compass
};

export function TerritoryNode({ territory, config, isRecommended, isCompact }) {
  const SymbolIcon = SYMBOLS[config.symbol] || Sun;
  const size = isCompact ? "w-12 h-12" : "w-16 h-16";
  const iconSize = isCompact ? "w-5 h-5" : "w-6 h-6";
  
  // Determine glow and opacity based on status
  const getStatusStyles = () => {
    switch (territory.status) {
      case "active":
        return {
          glow: `0 0 20px ${config.color}40, 0 0 40px ${config.color}20`,
          opacity: 1,
          borderColor: config.color
        };
      case "explored":
        return {
          glow: `0 0 10px ${config.color}30`,
          opacity: 0.9,
          borderColor: `${config.color}cc`
        };
      case "visited":
        return {
          glow: "none",
          opacity: 0.7,
          borderColor: `${config.color}80`
        };
      case "neglected":
      default:
        return {
          glow: "none",
          opacity: 0.4,
          borderColor: `${config.color}40`
        };
    }
  };

  const styles = getStatusStyles();

  return (
    <div
      className={`absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ${
        isRecommended ? "animate-pulse-slow" : ""
      }`}
      style={{
        left: `${config.position.x}%`,
        top: `${config.position.y}%`,
        opacity: styles.opacity
      }}
      data-testid={`territory-${territory.id}`}
    >
      {/* Medallion container */}
      <div 
        className={`${size} rounded-full flex items-center justify-center relative`}
        style={{
          background: `linear-gradient(145deg, #2a1810 0%, #1a0f08 100%)`,
          border: `2px solid ${styles.borderColor}`,
          boxShadow: styles.glow
        }}
      >
        {/* Inner decorative ring */}
        <div 
          className="absolute inset-1 rounded-full"
          style={{
            border: `1px solid ${config.color}30`
          }}
        />
        
        {/* Symbol */}
        <SymbolIcon 
          className={iconSize}
          style={{ color: config.color }}
        />

        {/* Engagement indicator (small arc) */}
        {territory.engagement_score > 0 && (
          <svg 
            className="absolute inset-0 w-full h-full -rotate-90"
            viewBox="0 0 100 100"
          >
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke={`${config.color}30`}
              strokeWidth="2"
            />
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke={config.color}
              strokeWidth="2"
              strokeDasharray={`${territory.engagement_score * 2.89} 289`}
              strokeLinecap="round"
            />
          </svg>
        )}

        {/* Recommended pulse ring */}
        {isRecommended && (
          <div 
            className="absolute inset-0 rounded-full animate-ping-slow"
            style={{
              border: `2px solid ${config.color}`,
              opacity: 0.5
            }}
          />
        )}
      </div>

      {/* Territory label - shows Quest name */}
      {!isCompact && (
        <div className="absolute left-1/2 -translate-x-1/2 mt-2 text-center w-24">
          <p 
            className="text-[10px] font-medium tracking-wide leading-tight"
            style={{ color: `${config.color}cc` }}
          >
            {config.questName || territory.name.split(" ").slice(0, 2).join(" ")}
          </p>
        </div>
      )}
    </div>
  );
}

export default TerritoryNode;
